import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Global scroll-reveal hook.
 * Auto-observes every element with [data-reveal] and toggles `.in-view`
 * when it enters the viewport. Re-scans on route change.
 *
 * Robust against fast scrolling and elements already past the viewport
 * (e.g., on refresh mid-page or with restored scroll position).
 */

// Module-level helpers keep the useEffect closure small and its dependency
// array trivial (only re-runs on route change).

function revealElement(el) {
  const delay = el.getAttribute("data-reveal-delay");
  if (delay && delay !== "0") el.style.transitionDelay = `${delay}ms`;
  el.classList.add("in-view");
}

function collectPendingReveals() {
  const els = Array.from(document.querySelectorAll("[data-reveal]:not(.in-view)"));
  const vh = window.innerHeight || document.documentElement.clientHeight;
  const toObserve = [];
  els.forEach((el) => {
    const rect = el.getBoundingClientRect();
    if (rect.top < vh && rect.bottom > 0) {
      revealElement(el);
    } else if (rect.bottom <= 0) {
      el.style.transition = "none";
      revealElement(el);
    } else {
      toObserve.push(el);
    }
  });
  return toObserve;
}

function buildObserver() {
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          revealElement(entry.target);
          io.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.05, rootMargin: "0px 0px -2% 0px" }
  );
  return io;
}

function sweepOnScroll(io) {
  const els = document.querySelectorAll("[data-reveal]:not(.in-view)");
  const vh = window.innerHeight;
  els.forEach((el) => {
    const rect = el.getBoundingClientRect();
    if (rect.top < vh * 0.95) {
      if (rect.bottom <= 0) el.style.transition = "none";
      revealElement(el);
      if (io) io.unobserve(el);
    }
  });
}

export default function useScrollReveal() {
  const { pathname } = useLocation();

  useEffect(() => {
    let io;

    const raf = requestAnimationFrame(() => {
      const pending = collectPendingReveals();
      if (!pending.length) return;
      io = buildObserver();
      pending.forEach((el) => io.observe(el));
    });

    const onScroll = () => sweepOnScroll(io);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      if (io) io.disconnect();
    };
    // `collectPendingReveals`, `buildObserver`, `sweepOnScroll` are module-level
    // constants; globals (`requestAnimationFrame`, `IntersectionObserver`, `window`)
    // are also stable. The linter's request to include them is a false positive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
}
